// src/pages/Setor2Page.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  MenuItem,
  Button,
  IconButton,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PainelDetalheCompras from './PainelDetalheCompras.jsx';
import GraficoRealVsRemu from './GraficoRealVsRemu.jsx';
import GraficoTendenciaMensal from './GraficoTendenciaMensal.jsx';
// Painel diário de compras (tooltip grande)
import PainelCompraConsumo from './PainelCompraConsumo';
// Hook de dados para VW_BSP_COMPRACONSUMO (usa a rota POST paginada)

// Ícones (apenas visuais; não mudam regra de negócio)
import AltRouteRoundedIcon from '@mui/icons-material/AltRouteRounded';
import DirectionsBusRoundedIcon from '@mui/icons-material/DirectionsBusRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import MonetizationOnRoundedIcon from '@mui/icons-material/MonetizationOnRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import FilterAltOffRoundedIcon from '@mui/icons-material/FilterAltOffRounded';


import Lottie from 'lottie-react';
import cartAnimation from '../assets/car.json';
import TabelaAnalise from '../components/TabelaAnalise';
import TabInsumoCompra from "./Tab_Insumo_Compra.jsx";
import { useThemeMode } from '../context/ThemeContext';

// Em desenvolvimento (localhost), usa '' para que /api/* passe pelo proxy.
// Em produção, usa a URL definida no .env.
const _isLocal = ['localhost', '127.0.0.1', '192.168.0.29'].includes(window.location.hostname);
const API_BASE = _isLocal ? '' : (process.env.REACT_APP_API_BASE || '');
console.log('🔍 Setor2Page - API_BASE (env):', process.env.REACT_APP_API_BASE);
console.log('🔍 Setor2Page - API_BASE (final):', API_BASE, '| isLocal:', _isLocal);

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export default function Setor2Page() {
  const { mode, setMode } = useThemeMode();
  const isDark = mode === 'dark';

  const [rows, setRows] = useState(null);

  const [error, setError] = useState(null);
  const [kmAcopTotal, setKmAcopTotal] = useState(null);
  const [frotaTotal, setFrotaTotal] = useState(null);
  const [kmAcopPorMes, setKmAcopPorMes] = useState({});
  const [loadingKmMes, setLoadingKmMes] = useState(false);
  const [compraConsumoRows, setCompraConsumoRows] = useState([]);
  const [loadingPainel, setLoadingPainel] = useState(false);


  const currentDate = dayjs();
  const [startDate, setStartDate] = useState(currentDate.startOf('month'));
  const [endDate, setEndDate] = useState(currentDate.endOf('month'));
  const [selectedMonth, setSelectedMonth] = useState(currentDate.month()); // 0-based: jan=0, fev=1…
  const [selectedGroup, setSelectedGroup] = useState(null); // ex.: "002 - PNEUS"
  const [selectedCompra, setSelectedCompra] = useState(null); // ex.: "018 - PNEUS NOVOS"  
  // Controle do Painel Diário
  const [openPainel, setOpenPainel] = useState(false);


  const selectedYear = useMemo(() => {
    const d = dayjs(startDate);
    return d.isValid() ? d.year() : dayjs().year();
  }, [startDate]);

  const navigate = useNavigate();
  const handleVoltarClick = () => navigate('/menu');

  useEffect(() => {
    if (!startDate || !endDate) return;

    let cancelled = false;

    const payload = {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
    };

    // importante: ao iniciar uma nova busca, limpamos o erro anterior
    setError(null);

    axios
      .post(`${API_BASE}/api/setor2/servicos`, payload, {
        headers: { Authorization: 'chave-super-secreta-123' },
      })
      .then((res) => {
        if (cancelled) return;

        // proteção: backend às vezes devolve objeto de erro com 200
        if (!Array.isArray(res.data)) {
          throw new Error('Resposta inválida em /servicos (esperado array).');
        }

        setRows(res.data);
      })
      .catch((err) => {
        if (cancelled) return;

        console.error('Erro na API:', err?.response || err?.message);

        // mantém mensagem consistente
        setError(
          err?.response
            ? `Erro ${err.response.status}: ${err.response.data?.error || err.response.statusText}`
            : 'Não foi possível carregar os dados'
        );
      });

    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);


  // Dados crus da VW_BSP_COMPRACONSUMO para o detalhamento de insumos
  useEffect(() => {
    if (!startDate || !endDate) return;
    let cancelled = false;

    const payload = {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate:   endDate.format('YYYY-MM-DD'),
    };

    setLoadingPainel(true);

    const MAX_RETRIES = 2;

    async function fetchCompraConsumo() {
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const res = await axios.post(
            `${API_BASE}/api/setor2/compra-consumo`,
            payload,
            {
              headers: { Authorization: 'chave-super-secreta-123' },
              timeout: 120000, // 2 min para respostas grandes
            }
          );
          if (cancelled) return;
          const data = Array.isArray(res.data) ? res.data : [];
          setCompraConsumoRows(data);
          return; // sucesso
        } catch (err) {
          if (cancelled) return;
          console.warn(
            `⚠️ compra-consumo tentativa ${attempt + 1}/${MAX_RETRIES + 1}:`,
            err?.code || err?.message
          );
          if (attempt === MAX_RETRIES) {
            console.error('❌ Erro ao buscar VW_BSP_COMPRACONSUMO para detalhamento:', err.response || err.message);
          } else {
            await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
          }
        }
      }
    }

    fetchCompraConsumo().finally(() => {
      if (!cancelled) setLoadingPainel(false);
    });

    return () => { cancelled = true; };
  }, [startDate, endDate]);

  useEffect(() => {
    if (startDate && endDate) {
      axios
        .post(
          `${API_BASE}/api/setor2/kmacop`,
          {
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD')
          },
          { headers: { Authorization: 'chave-super-secreta-123' } }
        )
        .then(res => {
          if (res.data && typeof res.data.total === 'number') {
            setKmAcopTotal(res.data.total);
          } else {
            setKmAcopTotal(0);
          }
        })
        .catch(err => {
          console.error('❌ Erro ao buscar KM ACOP:', err.response || err.message);
          setKmAcopTotal(0);
        });
    }
  }, [startDate, endDate]);

  useEffect(() => {
    let active = true;

    async function fetchKmPorMes() {
      try {
        setLoadingKmMes(true);

        const promises = Array.from({ length: 12 }, (_, i) => {
          const m = i + 1;
          const ini = dayjs(`${selectedYear}-${String(m).padStart(2, '0')}-01`).startOf('month');
          const fim = ini.endOf('month');

          return axios.post(
            `${API_BASE}/api/setor2/kmacop`,
            {
              startDate: ini.format('YYYY-MM-DD'),
              endDate:   fim.format('YYYY-MM-DD'),
            },
            { headers: { Authorization: 'chave-super-secreta-123' } }
          );
        });

        const results = await Promise.all(promises);
        if (!active) return;

        const map = {};
        results.forEach((res, idx) => {
          const mes = idx + 1;
          const total = Number(res?.data?.total ?? 0);
          map[mes] = Number.isFinite(total) ? total : 0;
        });

        setKmAcopPorMes(map);
      } catch (e) {
        console.error('Erro ao carregar KM por mês:', e);
        setKmAcopPorMes({});
      } finally {
        if (active) setLoadingKmMes(false);
      }
    }

    fetchKmPorMes();
    return () => { active = false; };
  }, [selectedYear]);


  useEffect(() => {
    const fetchFrotaTotal = async () => {
      try {
        const payload = {};
        if (startDate && endDate) {
          payload.startDate = startDate.format('YYYY-MM-DD');
          payload.endDate = endDate.format('YYYY-MM-DD');
        }
        const res = await axios.post(`${API_BASE}/api/setor2/frota-total`, payload, {
          headers: { Authorization: 'chave-super-secreta-123' }
        });
        setFrotaTotal(res.data.total);
      } catch (err) {
        console.error('Erro ao buscar Frota Total:', err.response || err.message);
      }
    };
    fetchFrotaTotal();
  }, [startDate, endDate]);

  const handleMonthChange = (e) => {
    const val = e.target.value;

    // voltou para "vazio": não filtra por mês, mantém o intervalo atual de datas
    if (val === '') {
      setSelectedMonth('');
      return;
    }

    const month = parseInt(val, 10);
    const newStart = dayjs(`${selectedYear}-${String(month + 1).padStart(2, '0')}-01`).startOf('month');
    const newEnd = dayjs(`${selectedYear}-${String(month + 1).padStart(2, '0')}-01`).endOf('month');

    setSelectedMonth(month);
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  const handleRestaurar = () => {
    const now = dayjs();

    // volta para o padrão de quando entra na página
    setStartDate(dayjs(`${now.year()}-01-01`));
    setEndDate(now);

    // mês em branco (sem filtro por mês)
    setSelectedMonth('');

    // limpa seleções internas
    setSelectedGroup(null);
    setSelectedCompra(null);

    // fecha painel diário se estiver aberto
    setOpenPainel(false);
  };

  //console.log('Tipo de PainelDetalheCompras:', typeof PainelDetalheCompras);

  const filteredRows =useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return rows.filter(row => {
      const rowDate = dayjs(row.DATA);
      if (!rowDate.isValid()) return false;
      return rowDate.isSameOrAfter(startDate, 'day') && rowDate.isSameOrBefore(endDate, 'day');
    });
  }, [rows, startDate, endDate]);

  console.log("filteredRows keys:", filteredRows?.[0] ? Object.keys(filteredRows[0]) : "filteredRows vazio");

  useEffect(() => {
    if (
      selectedGroup &&
      !filteredRows.some(r => String(r.GR_ANALISE).trim() === String(selectedGroup).trim())
    ) {
      setSelectedGroup(null);
      setSelectedCompra(null);
    }
  }, [filteredRows, selectedGroup]);

  const immuGrupoSelecionado = useMemo(() => {
    if (!selectedGroup || !selectedCompra) return 0;

    return filteredRows
      .filter(r =>
        String(r.GR_ANALISE || '').trim() === String(selectedGroup || '').trim() &&
        String(r.GR_COMPRA || '').trim()  === String(selectedCompra || '').trim()
      )
      .reduce((sum, r) => sum + Number(r.IMMU || 0), 0);
  }, [filteredRows, selectedGroup, selectedCompra]);

  const totalRealizado = useMemo(() => {
    return filteredRows.reduce((sum, row) => {
      return sum + Number(row.GASTO_REAL || 0);
    }, 0);
  }, [filteredRows]);

  const gastoProgramado = useMemo(() => {
    return filteredRows.reduce((sum, row) => sum + Number(row.META || 0), 0);
  }, [filteredRows]);
    
  // Custo IMMU (R$/km) correto: média ponderada => Σ(IMMU_mês*KM_mês) / Σ(KM_mês)
  const totalImmu = useMemo(() => {
    if (!filteredRows || filteredRows.length === 0) return 0;

    // Numerador: Σ(IMMU_mês * KM_mês)
    const numerador = (() => {
      const immuPorMes = filteredRows.reduce((acc, r) => {
        const d = dayjs(r.DATA);
        if (!d.isValid()) return acc;

        const mes = d.month() + 1;
        acc[mes] = (acc[mes] || 0) + Number(r.IMMU ?? 0);
        return acc;
      }, {});

      return Object.entries(immuPorMes).reduce((sum, [mesStr, immuMes]) => {
        const mes = Number(mesStr);
        const kmMes = Number(kmAcopPorMes?.[mes] ?? 0);
        return sum + (Number(immuMes) * kmMes);
      }, 0);
    })();

    // Denominador: KM total do período filtrado (você já busca esse número certinho)
    const kmPeriodo = Number(kmAcopTotal ?? 0);
    if (!kmPeriodo) return 0;

    return numerador / kmPeriodo;
  }, [filteredRows, kmAcopPorMes, kmAcopTotal]);


  const custoEmpresa = useMemo(() => {
    if (!kmAcopTotal || kmAcopTotal === 0) return 0;
    return totalRealizado / kmAcopTotal;
  }, [totalRealizado, kmAcopTotal]);

  const difEmpresaImmuPercent = totalImmu > 0 ? ((custoEmpresa - totalImmu) / totalImmu) * 100 : 0;

  // Custo Remunerado IMMU (correto): Σ(IMMU_mês * KM_mês) no intervalo
  const custoRemuneradoImmu = useMemo(() => {
    if (!filteredRows || filteredRows.length === 0) return 0;

    // soma IMMU por mês (mês = 1..12)
    const immuPorMes = filteredRows.reduce((acc, r) => {
      const d = dayjs(r.DATA);
      if (!d.isValid()) return acc;

      const mes = d.month() + 1; // 1..12
      acc[mes] = (acc[mes] || 0) + Number(r.IMMU ?? 0);
      return acc;
    }, {});

    // calcula Σ(IMMU_mês * KM_mês) usando o mapa kmAcopPorMes
    return Object.entries(immuPorMes).reduce((sum, [mesStr, immuMes]) => {
      const mes = Number(mesStr);
      const kmMes = Number(kmAcopPorMes?.[mes] ?? 0);
      return sum + (Number(immuMes) * kmMes);
    }, 0);
  }, [filteredRows, kmAcopPorMes]);

  // Diferença: REALIZADO vs CUSTO REMUNERADO IMMU
  const diferenca = totalRealizado - custoRemuneradoImmu;
  const diferencaPercentual =
    custoRemuneradoImmu > 0 ? (diferenca / custoRemuneradoImmu) * 100 : 0;

  const iconeDiferenca =
    diferenca > 0 ? (
      <ArrowDropUpIcon
        sx={{ color: 'error.main', verticalAlign: 'middle', fontSize: '1.5rem' }}
      />
    ) : (
      <ArrowDropDownIcon
        sx={{ color: 'success.main', verticalAlign: 'middle', fontSize: '1.5rem' }}
      />
    );

  // Seta (↑/↓) para o card "Dif. Empresa vs IMMU"
  const deltaVsImmu = custoEmpresa - totalImmu;
  const iconeVsImmu =
    totalImmu > 0
      ? (deltaVsImmu > 0 ? (
          <ArrowDropUpIcon sx={{ color: 'error.main', verticalAlign: 'middle', fontSize: '1.5rem' }} />
        ) : (
          <ArrowDropDownIcon sx={{ color: 'success.main', verticalAlign: 'middle', fontSize: '1.5rem' }} />
        ))
      : null;


  if (error && !rows) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!rows) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Helpers de formatação (apenas visual)
  const fmt2 = (n) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtBRL = (n) => Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fmtBRL4 = (n) =>
    Number(n || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });


  // Paleta de fundo por card (estilo Tráfego: claro no Light / sólido no Dark)
  const BG = {
    kmAcop: isDark ? '#1e88e5' : '#e3f2fd',
    frota: isDark ? '#455a64' : '#eceff1',
    programado: isDark ? '#8e24aa' : '#ede7f6',
    real: isDark ? '#1976d2' : '#e3f2fd',
    diff: isDark ? '#00acc1' : '#e0f7fa',
    immu: isDark ? '#FFA700' : '#FFFB96',
    remuImmu: isDark ? '#008080' : '#BAFFC9',
    empresa: isDark ? '#0288d1' : '#e1f5fe',
    vs: isDark ? '#5e35b1' : '#DABCFF',
  };

  const indicadores = [
    {
      label: 'KM ACOP REALIZADA',
      valor: kmAcopTotal != null ? fmt2(kmAcopTotal) : 'Carregando...',
      icon: <AltRouteRoundedIcon />,
      bg: BG.kmAcop,
    },
    {
      label: 'FROTA OPERACIONAL',
      valor: frotaTotal != null ? Number(frotaTotal).toLocaleString('pt-BR') : 'Carregando...',
      icon: <DirectionsBusRoundedIcon />,
      bg: BG.frota,
    },
    {
      label: 'IMMU',
      valor: (custoRemuneradoImmu != null) ? fmtBRL(custoRemuneradoImmu) : 'Carregando...',
      icon: <MonetizationOnRoundedIcon />,
      bg: BG.remuImmu,
    },    
    {
      label: 'PROGRAMADO',
      valor: fmtBRL(gastoProgramado),
      icon: <AssignmentRoundedIcon />,
      bg: BG.programado,
    },
    {
      label: 'REALIZADO',
      valor: fmtBRL(totalRealizado),
      icon: <ReceiptLongRoundedIcon />,
      bg: BG.real,
    },
    {
      label: 'DIFERENÇA',
      valor: (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          {fmtBRL(diferenca)} {custoRemuneradoImmu > 0 && iconeDiferenca}
        </Box>
      ),
      sub:
        custoRemuneradoImmu > 0
          ? `${Math.abs(diferencaPercentual).toFixed(2).replace('.', ',')}% ${
              diferenca > 0 ? 'Acima' : 'Abaixo'
            } do IMMU`
          : null,
      icon: <TrendingUpRoundedIcon />,
      bg: BG.diff,
    },

    {
      label: 'CUSTO IMMU',
      valor: fmtBRL4(totalImmu),
      icon: <MonetizationOnRoundedIcon />,
      bg: BG.immu,
    },
    {
      label: 'CUSTO EMRPRESA',
      valor: kmAcopTotal != null ? fmtBRL(custoEmpresa) : 'Carregando...',
      icon: <BusinessRoundedIcon />,
      bg: BG.empresa,
    },
    {
      label: 'Dif. EMPRESA x IMMU',
      valor: (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          {fmtBRL(deltaVsImmu)} {iconeVsImmu}
        </Box>
      ),
      sub:
        totalImmu > 0
          ? `${Math.abs(difEmpresaImmuPercent).toFixed(2).replace('.', ',')}% ${
              deltaVsImmu > 0 ? 'Acima' : 'Abaixo'
            } do IMMU`
          : null,
      icon: <CompareArrowsRoundedIcon />,
      bg: BG.vs,
    },

  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 4 }}>
      {/* FAIXA SUPERIOR (sem Atualizar/contador) */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          {/* Esquerda: Lottie + filtros */}
          <Grid item sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
            <Box sx={{ width: 120, mr: 1 }}>
              <Lottie animationData={cartAnimation} loop autoplay style={{ width: '100%', height: '100%' }} />
            </Box>

            <DatePicker
              label="Data Início"
              value={startDate}
              onChange={(v) => { setStartDate(v); setSelectedMonth(''); }}
              format="DD/MM/YYYY"
              slotProps={{ textField: { sx: { width: 160 } } }}
            />
            <DatePicker
              label="Data Fim"
              value={endDate}
              onChange={(v) => { setEndDate(v); setSelectedMonth(''); }}
              format="DD/MM/YYYY"
              slotProps={{ textField: { sx: { width: 160, ml: 1 } } }}
            />

            <TextField
              select
              label="Mês"
              value={selectedMonth}
              onChange={handleMonthChange}
              sx={{ width: 160, ml: 1 }}
            >
              <MenuItem value="">
                <em>Selecione…</em>
              </MenuItem>

              {[
                'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
                'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'
              ].map((month, index) => (
                <MenuItem key={index} value={index}>{month}</MenuItem>
              ))}
            </TextField>

            <Button
              variant="contained"
              color="info"
              onClick={() => setOpenPainel(true)}
              disabled={!!loadingPainel}
              sx={{ ml: 1, height: 40, whiteSpace: 'nowrap' }}
            >
              {loadingPainel ? 'Carregando…' : 'Painel Diário'}
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              startIcon={<FilterAltOffRoundedIcon />}
              onClick={() => setSelectedGroup(null)}
              disabled={!selectedGroup}
              sx={{ ml: 1, height: 40, whiteSpace: 'nowrap' }}
            >
              Limpar filtro
            </Button>

            <Button
              variant="outlined"
              color="inherit"
              onClick={handleRestaurar}
              sx={{ ml: 1, height: 40, whiteSpace: 'nowrap' }}
            >
              Restaurar
            </Button>
          </Grid>

          {/* Direita: tema + voltar */}
          <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              onClick={() => setMode(isDark ? 'light' : 'dark')}
              color="inherit"
              aria-label="Alternar tema"
              sx={{ border: '1px solid', borderColor: 'divider' }}
            >
              {isDark ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
            </IconButton>

            <Button variant="outlined" color="primary" onClick={handleVoltarClick} aria-label="Voltar ao Menu" sx={{ height: 40 }}>
              Voltar
            </Button>
          </Grid>
        </Grid>
      </LocalizationProvider>

      {/* KPI STRIP — Layout de Tráfego */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
        {indicadores.map((item, idx) => (
          <Paper
            key={idx}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              minWidth: 240,
              backgroundColor: item.bg,
              borderLeft: '6px solid #1976d2',
              borderRadius: 2,
              boxShadow: 3
            }}
          >
            {/* Ícone */}
            <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {item.icon}
            </Box>

            {/* Valor + Label (+ sub opcional) */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                {item.valor}
              </Typography>
              <Typography
                variant="body2"
                sx={(t) => ({ color: t.palette.mode === 'dark' ? '#FFFFFF' : t.palette.text.secondary })}
              >
                {item.label}
              </Typography>

              {item.sub && (
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 0.25 }}>
                  {item.sub}
                </Typography>
              )}
            </Box>
          </Paper>
        ))}
      </Box>

      {/* TABELA — somente o painel de fundo com borda azul; sem borda externa; sem título */}
      <Box sx={{ mt: 4 }}>
        <Box
          sx={{
            p: 2,                                  // respiro interno p/ a tabela não encostar
            border: '2px solid',
            borderColor: 'info.main',              // contorno AZUL do painel
            borderRadius: 4,
            boxShadow: 8,
            bgcolor: (t) => (t.palette.mode === 'dark' ? '#1E1E1E' : t.palette.grey[100]) // “segunda tela”
          }}
        >
          <Box sx={{ borderRadius: 3, overflow: 'hidden' /* sem borda aqui */ }}>
            <TabelaAnalise
              rows={filteredRows}
              kmAcopTotal={kmAcopTotal}
              kmAcopPorMes={kmAcopPorMes}
              onSelectGroup={setSelectedGroup}
              selectedGroup={selectedGroup}
            />

            <PainelDetalheCompras
              rows={filteredRows}
              selectedGroup={selectedGroup}
              kmAcopTotal={kmAcopTotal}
              kmAcopPorMes={kmAcopPorMes}
              selectedCompra={selectedCompra}
              onSelectCompra={setSelectedCompra}
            />

            <TabInsumoCompra
              rows={compraConsumoRows}
              selectedGroup={selectedGroup}
              selectedCompra={selectedCompra}
              kmAcopTotal={kmAcopTotal}
              immuGrupo={immuGrupoSelecionado}
            />

            <GraficoRealVsRemu
              rows={filteredRows}
              selectedGroup={selectedGroup}
              kmAcopTotal={kmAcopTotal}
            />
            {loadingKmMes ? (
              <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                Carregando tendência mensal…
              </Box>
            ) : (
              <GraficoTendenciaMensal
                rows={rows}            // usa a base completa; o componente filtra por ANO internamente
                startDate={startDate}  // ele extrai apenas o ano e ignora o mês
                kmAcopPorMes={kmAcopPorMes}
              />
            )}
            {/* Painel Diário de Compras (instância única, fora do .map) */}
            <PainelCompraConsumo
              open={openPainel}
              onClose={() => setOpenPainel(false)}
              ano={selectedYear}
              mes={selectedMonth + 1}
              rows={compraConsumoRows}
              enableAutoLoad={false}
            />
          </Box>
        </Box>
      </Box>

    </Box>
  );
}